import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { postOrder, resolveImageUrl } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export const CartDrawer = () => {
  const { items, updateQty, removeItem, clearCart, total, count, isOpen, setIsOpen } = useCart();
  const { t, lang } = useLang();
  const czk = (n) => (lang === "en" ? `${n.toLocaleString("en-GB")} CZK` : `${n.toLocaleString("cs-CZ")} Kč`);
  const [step, setStep] = useState("cart"); // cart | form | success
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    zip_code: "",
    note: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (!open) {
      setTimeout(() => setStep(items.length ? "cart" : "cart"), 300);
    }
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.email || !form.street || !form.city || !form.zip_code) {
      toast.error(t("cart.errRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await postOrder({
        ...form,
        items: items.map(({ product_id, name, price, qty, size }) => ({
          product_id,
          name,
          price,
          qty,
          size,
        })),
      });
      setOrder(res.order);
      clearCart();
      setStep("success");
      toast.success(t("cart.success"));
    } catch (err) {
      toast.error(t("cart.errSubmit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        data-testid="cart-drawer"
        className="flex w-full flex-col border-white/10 bg-[#0B0D10] sm:max-w-md"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-2xl uppercase tracking-[0.1em] text-white">
            <ShoppingBag className="h-5 w-5 text-[#B7FF2A]" />
            {step === "success" ? t("cart.done") : step === "form" ? t("cart.order") : t("cart.cart")}
            {step === "cart" && count > 0 && (
              <span className="font-mono text-xs text-white/40">({count})</span>
            )}
          </SheetTitle>
        </SheetHeader>

        {step === "cart" && (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center" data-testid="cart-empty-state">
                  <ShoppingBag className="h-10 w-10 text-white/20" />
                  <p className="text-sm text-white/50">{t("cart.empty")}<br />{t("cart.emptyHint")}</p>
                </div>
              ) : (
                items.map((i) => (
                  <div
                    key={i.key}
                    data-testid={`cart-item-${i.product_id}`}
                    className="clip-corner-sm flex gap-3 border border-white/10 bg-[#0F1217] p-3"
                  >
                    <img
                      src={resolveImageUrl(i.image)}
                      alt={i.name}
                      className="h-16 w-16 shrink-0 rounded object-cover"
                      loading="lazy"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-white">{i.name}</p>
                      {i.size && (
                        <p className="font-mono text-xs text-white/45">{t("cart.size")}: {i.size}</p>
                      )}
                      <p className="mt-1 font-mono text-sm text-[#B7FF2A]">{czk(i.price)}</p>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button
                        onClick={() => removeItem(i.key)}
                        data-testid={`cart-item-remove-${i.product_id}`}
                        className="text-white/35 transition-colors hover:text-[#FF3B3B]"
                        aria-label={t("cart.remove")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQty(i.key, -1)}
                          data-testid={`cart-item-minus-${i.product_id}`}
                          className="flex h-6 w-6 items-center justify-center rounded border border-white/15 text-white/70 hover:border-white/40"
                          aria-label={t("cart.minus")}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-5 text-center font-mono text-sm">{i.qty}</span>
                        <button
                          onClick={() => updateQty(i.key, 1)}
                          data-testid={`cart-item-plus-${i.product_id}`}
                          className="flex h-6 w-6 items-center justify-center rounded border border-white/15 text-white/70 hover:border-white/40"
                          aria-label={t("cart.plus")}
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {items.length > 0 && (
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between">
                  <span className="font-display text-lg uppercase tracking-[0.1em] text-white/70">{t("cart.total")}</span>
                  <span data-testid="cart-total" className="font-mono text-xl font-semibold text-[#B7FF2A]">
                    {czk(total)}
                  </span>
                </div>
                <Button
                  data-testid="cart-checkout-button"
                  onClick={() => setStep("form")}
                  className="light-sweep clip-corner-sm mt-4 h-12 w-full bg-[#1EA7FF] font-display text-base uppercase tracking-[0.14em] text-[#07080A] hover:bg-[#1EA7FF] hover:brightness-110"
                >
                  {t("cart.checkout")}
                </Button>
              </div>
            )}
          </>
        )}

        {step === "form" && (
          <form onSubmit={submitOrder} className="flex flex-1 flex-col gap-3 overflow-y-auto py-4" data-testid="checkout-form">
            <button
              type="button"
              onClick={() => setStep("cart")}
              data-testid="checkout-back-button"
              className="flex items-center gap-1.5 self-start font-mono text-xs uppercase tracking-[0.2em] text-white/45 hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> {t("cart.back")}
            </button>
            <Input data-testid="checkout-name-input" placeholder={t("cart.phName")} value={form.customer_name} onChange={set("customer_name")} className="h-11 border-white/15 bg-[#0F1217]" />
            <Input data-testid="checkout-email-input" type="email" placeholder={t("cart.phEmail")} value={form.email} onChange={set("email")} className="h-11 border-white/15 bg-[#0F1217]" />
            <Input data-testid="checkout-phone-input" placeholder={t("cart.phPhone")} value={form.phone} onChange={set("phone")} className="h-11 border-white/15 bg-[#0F1217]" />
            <Input data-testid="checkout-street-input" placeholder={t("cart.phStreet")} value={form.street} onChange={set("street")} className="h-11 border-white/15 bg-[#0F1217]" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="checkout-city-input" placeholder={t("cart.phCity")} value={form.city} onChange={set("city")} className="h-11 border-white/15 bg-[#0F1217]" />
              <Input data-testid="checkout-zip-input" placeholder={t("cart.phZip")} value={form.zip_code} onChange={set("zip_code")} className="h-11 border-white/15 bg-[#0F1217]" />
            </div>
            <Textarea data-testid="checkout-note-input" placeholder={t("cart.phNote")} value={form.note} onChange={set("note")} className="min-h-20 border-white/15 bg-[#0F1217]" />
            <div className="mt-auto border-t border-white/10 pt-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm text-white/55">{count} {t("cart.itemsCount")}</span>
                <span className="font-mono text-lg font-semibold text-[#B7FF2A]">{czk(total)}</span>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="checkout-submit-order-button"
                className="light-sweep clip-corner-sm h-12 w-full bg-[#B7FF2A] font-display text-base uppercase tracking-[0.14em] text-[#07080A] hover:bg-[#B7FF2A] hover:brightness-110 disabled:opacity-60"
              >
                {submitting ? (
                  <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t("cart.sending")}</span>
                ) : (
                  t("cart.submit")
                )}
              </Button>
              <p className="mt-2 text-center font-mono text-[10px] text-white/35">{t("cart.paymentNote")}</p>
            </div>
          </form>
        )}

        {step === "success" && order && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center" data-testid="checkout-success">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#B7FF2A]/10">
              <CheckCircle2 className="h-8 w-8 text-[#B7FF2A]" />
            </div>
            <div>
              <p className="font-display text-2xl uppercase tracking-[0.1em]">{t("cart.accepted")}</p>
              <p className="mt-1 text-sm text-white/55">
                {t("cart.orderNumber")}{" "}
                <span data-testid="checkout-order-number" className="font-mono text-[#B7FF2A]">
                  {order.order_number}
                </span>
              </p>
              <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/45">{t("cart.thanks")}</p>
            </div>
            <Button
              data-testid="checkout-success-close-button"
              onClick={() => { setIsOpen(false); setTimeout(() => setStep("cart"), 300); }}
              variant="outline"
              className="clip-corner-sm border-white/20 font-display uppercase tracking-[0.14em] hover:bg-white/5"
            >
              {t("cart.close")}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
